import { createRouter, RouterProvider } from "@tanstack/react-router"
import { createRoot } from "react-dom/client"

import "@/app/i18n"
import { ErrorBoundary } from "@/app/error-boundary"
import { getEnv } from "@/lib/utils/env"
import { routeTree } from "./routeTree.gen"

import "@/styles/globals.css"

// Stale chunk recovery — eski sekme yeni deploy'la çakıştığında hash'lenmiş
// chunk dosyaları 404 döner ("Failed to fetch dynamically imported module").
// Vite preload başarısız olduğunda emit edilen event'i yakalayıp sayfayı
// yeniden yükle. sessionStorage guard sonsuz reload loop'una karşı; 5sn
// başarılı render sonrası flag temizlenir (sonraki deploy'da yine çalışsın).
const RELOAD_FLAG = "ok-stale-chunk-reload"
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault()
  if (sessionStorage.getItem(RELOAD_FLAG)) {
    return
  }
  sessionStorage.setItem(RELOAD_FLAG, "1")
  window.location.reload()
})
window.setTimeout(() => sessionStorage.removeItem(RELOAD_FLAG), 5000)

// Validate env early
getEnv()

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  // WP5 — View Transitions OPT-IN per route (ADR #015 revize).
  // `defaultViewTransition: true` DOM snapshot'ı canvas'ı (DevicesGlobe WebGL)
  // boş yakalıyordu: sayfa açılışında globe 1 frame gözüküp kayboluyordu.
  // Şu an globally kapalı; gerekirse route bazlı `createFileRoute({ viewTransition: true })`.
  // CSS `::view-transition-old/new(root)` tanımları `globals.css`'te kalıyor —
  // tarayıcı veya app code explicit `startViewTransition()` çağırırsa çalışır.
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

async function bootstrap() {
  // MSW — `VITE_MOCK_API` flag'i ile aç/kapa. Branch-aware build (Jenkinsfile)
  // dev branch'i için `true`, stg branch'i için `false` set eder; local dev'de
  // `.env` belirler. Production build'de de etkili olur (eski DEV-only gate
  // kaldırıldı).
  if (getEnv().VITE_MOCK_API) {
    const { worker } = await import("@/mocks/browser")
    await worker.start({ onUnhandledRequest: "bypass" })
  }

  const rootEl = document.getElementById("root")
  if (!rootEl) {
    throw new Error("Root element not found")
  }

  // NOTE: `<StrictMode>` kaldırıldı (2026-04-15, ADR #017).
  // Dev'de effects double-invoke `react-globe.gl` three.js context'ini bozuyor;
  // globe 1 frame render olup kayboluyordu. StrictMode'un sağladığı "future
  // correctness" garantileri bizim açımızdan değerli ama globe demo-kritik.
  // Faz 2'de globe'u StrictMode-safe yapılana dek opt-out.
  createRoot(rootEl).render(
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>,
  )
}

void bootstrap()
